import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail, CheckCircle } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    
    // Simulate API call - in production this would send a reset email
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setLoading(false);
    setSubmitted(true);
    toast.success("If an account exists, you will receive a reset link");
  };

  return (
    <div className="min-h-screen bg-background grid-pattern flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[128px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Back Link */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_31e6f2da-e30e-4604-a2ae-ae4870f67bdf/artifacts/m4cp44gx_passivepay_new_logo.png" 
              alt="PassivePay" 
              className="w-16 h-16 rounded-full object-cover mx-auto mb-4"
            />
            <h1 className="text-2xl font-heading font-bold">Reset Password</h1>
            <p className="text-muted-foreground mt-2">
              {submitted 
                ? "Check your email for reset instructions"
                : "Enter your email to receive a reset link"
              }
            </p>
          </div>

          {submitted ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <p className="text-muted-foreground">
                If an account exists with <span className="text-primary font-medium">{email}</span>, 
                you will receive a password reset link shortly.
              </p>
              <p className="text-sm text-muted-foreground">
                Didn't receive an email? Check your spam folder or contact support.
              </p>
              <Link to="/login">
                <Button className="w-full neon-glow font-heading font-bold uppercase tracking-wider">
                  Return to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-background/50 border-white/10 focus:border-primary h-12"
                  data-testid="forgot-email"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full neon-glow btn-glow font-heading font-bold uppercase tracking-wider h-12"
                data-testid="forgot-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" /> Send Reset Link
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Contact Support */}
          {!submitted && (
            <p className="text-center text-muted-foreground mt-6 text-sm">
              Need help?{" "}
              <Link to="/contact" className="text-primary hover:underline font-medium">
                Contact Support
              </Link>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
